import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../api';
import { LOCAL_SUTRAS, SUTTA_CENTRAL_CATALOG } from '../../data/sutraCatalog';
import { fetchSuttaCentralCatalogDirect, fetchSuttaCentralSutra as fetchSuttaCentralDirect } from '../../utils/suttaCentralClient';

const FEATURED_SUTRAS = [
    { uid: 'mn1', label: 'MN 1 - The Root of All Things' },
    { uid: 'dn1', label: 'DN 1 - The Prime Net' },
    { uid: 'sn56.11', label: 'SN 56.11 - Rolling Forth the Wheel of Dhamma' },
    { uid: 'an3.65', label: 'AN 3.65 - With the Kesaputtiyas' },
];

const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'zh', label: 'Chinese' },
];

const LOAD_MODE_OPTIONS = [
    { value: 'collection', label: 'By Collection' },
    { value: 'all', label: 'All' },
    { value: 'subject', label: 'By Subject' },
    { value: 'translator', label: 'By Translator' },
    { value: 'length', label: 'By Length' },
];

const COLLECTION_OPTIONS = [
    { value: 'mn', label: 'Middle Discourses' },
    { value: 'dn', label: 'Long Discourses' },
    { value: 'sn', label: 'Linked Discourses' },
    { value: 'an', label: 'Numbered Discourses' },
    { value: 'kn', label: 'Minor Collection' },
];

const SUBJECT_OPTIONS = [
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'meditation', label: 'Meditation' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'wisdom', label: 'Wisdom' },
    { value: 'suffering', label: 'Suffering' },
    { value: 'emptiness', label: 'Emptiness' },
];

const LENGTH_OPTIONS = [
    { value: 'dn', label: 'Long' },
    { value: 'mn', label: 'Middle' },
    { value: 'kn', label: 'Short / Minor' },
];

function splitFullText(fullText) {
    return String(fullText || '')
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);
}

function isLocalBrowser() {
    return typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

const Sutras = () => {
    const navigate = useNavigate();
    const { source, id } = useParams();
    const detailSource = source || null;
    const detailId = id ? decodeURIComponent(id) : null;
    const isDetailPage = Boolean(detailSource && detailId);
    const [uid, setUid] = useState('mn1');
    const [author, setAuthor] = useState('sujato');
    const [language, setLanguage] = useState('en');
    const [localSutras, setLocalSutras] = useState([]);
    const [suttaCentralCatalog, setSuttaCentralCatalog] = useState(SUTTA_CENTRAL_CATALOG);
    const [sutra, setSutra] = useState(null);
    const [activeSource, setActiveSource] = useState('local');
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [sourcePanelOpen, setSourcePanelOpen] = useState(false);
    const [loadMode, setLoadMode] = useState('collection');
    const [collectionRoot, setCollectionRoot] = useState('mn');
    const [subjectFilter, setSubjectFilter] = useState('mindfulness');
    const [translatorFilter, setTranslatorFilter] = useState('sujato');
    const [lengthRoot, setLengthRoot] = useState('dn');
    const [catalogStatus, setCatalogStatus] = useState('Featured sutras are loaded. Choose a load option for a larger catalog.');
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.body.style.backgroundImage = 'url("/images/library.jpg")';
        return () => { document.body.style.backgroundImage = 'none'; };
    }, []);

    const fetchSuttaCentralSutra = useCallback(async (requestedUid, requestedAuthor = 'sujato', requestedLanguage = language) => {
        const normalizedUid = String(requestedUid || '').trim().toLowerCase();
        const normalizedAuthor = String(requestedAuthor || 'sujato').trim().toLowerCase();
        const normalizedLanguage = String(requestedLanguage || 'en').trim().toLowerCase();

        if (!normalizedUid) {
            setError('Enter a SuttaCentral UID, such as mn1 or sn56.11.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                includeSegments: 'false',
                author: normalizedAuthor,
                lang: normalizedLanguage,
            });
            let data = {};
            try {
                if (isLocalBrowser()) {
                    data = {
                        sutra: await fetchSuttaCentralDirect(normalizedUid, normalizedAuthor, normalizedLanguage),
                    };
                } else {
                    const response = await fetch(`${API_URL}/sutras/suttacentral/${encodeURIComponent(normalizedUid)}?${params.toString()}`);
                    data = await response.json().catch(() => ({}));

                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to load sutra from SuttaCentral.');
                    }
                }
            } catch (apiErr) {
                console.warn('Buddha Wisdom API unavailable, loading SuttaCentral directly:', apiErr);
                data = {
                    sutra: await fetchSuttaCentralDirect(normalizedUid, normalizedAuthor, normalizedLanguage),
                };
            }

            if (!data.sutra) {
                throw new Error('The SuttaCentral response did not include sutra content.');
            }

            setUid(normalizedUid);
            setAuthor(normalizedAuthor);
            setLanguage(normalizedLanguage);
            setSutra(data.sutra);
            setActiveSource('suttacentral');
        } catch (err) {
            console.error('Error fetching SuttaCentral sutra:', err);
            setSutra(null);
            setError(err.message || 'Failed to load sutra. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [language]);

    const fetchLocalSutras = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (isLocalBrowser()) {
                setLocalSutras(LOCAL_SUTRAS);
                if (!isDetailPage) {
                    setSutra(LOCAL_SUTRAS[0]);
                    setActiveSource('local');
                }
                return;
            }

            const response = await fetch(`${API_URL}/sutras?limit=50`);
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.message || 'Failed to load manually entered sutras.');
            }

            const sutras = Array.isArray(data.sutras) && data.sutras.length > 0 ? data.sutras : LOCAL_SUTRAS;
            setLocalSutras(sutras);

            if (sutras.length > 0 && !isDetailPage) {
                setSutra(sutras[0]);
                setActiveSource('local');
                return;
            }

            if (!isDetailPage) {
                await fetchSuttaCentralSutra('mn1', 'sujato');
            }
        } catch (err) {
            console.error('Error fetching local sutras:', err);
            setLocalSutras(LOCAL_SUTRAS);
            if (!isDetailPage) {
                await fetchSuttaCentralSutra('mn1', 'sujato');
            }
        } finally {
            setLoading(false);
        }
    }, [fetchSuttaCentralSutra, isDetailPage]);

    const fetchSuttaCentralCatalog = useCallback(async (options = {}) => {
        const params = new URLSearchParams({
            root: options.root || 'mn',
            lang: language,
            includeBranches: 'false',
        });
        if (options.subject) {
            params.set('subject', options.subject);
        }
        if (options.translator) {
            params.set('translator', options.translator);
        }

        const urls = [
            `${API_URL}/sutras/suttacentral/catalog?${params.toString()}`,
        ];

        if (isLocalBrowser()) {
            urls.unshift(`/local-api/suttacentral/catalog?${params.toString()}`);
        }

        for (const url of urls) {
            try {
                setCatalogLoading(true);
                setCatalogStatus('Loading SuttaCentral catalog...');
                const response = await fetch(url);
                const data = await response.json().catch(() => ({}));

                if (!response.ok || !Array.isArray(data.catalog)) {
                    continue;
                }

                const normalizedCatalog = data.catalog
                    .filter((item) => item.uid && Number(item.yellow_brick_road_count || 0) > 0)
                    .map((item) => ({
                        uid: item.uid,
                        title: item.title,
                        collection: item.collection,
                        blurb: item.blurb,
                        root_title: item.root_title,
                        breadcrumbs: item.breadcrumbs || [],
                        translator: item.translator,
                        translator_uid: item.translator_uid,
                        yellow_brick_road_count: item.yellow_brick_road_count,
                        sourceType: 'suttacentral',
                        searchText: `${item.uid} ${item.title || ''} ${item.short_title || ''} ${item.collection || ''} ${item.blurb || ''} ${(item.breadcrumbs || []).join(' ')} ${item.translator || ''} ${item.translator_uid || ''}`.toLowerCase(),
                    }));

                if (normalizedCatalog.length > 0) {
                    setSuttaCentralCatalog(normalizedCatalog);
                    setCatalogStatus(`Loaded ${normalizedCatalog.length.toLocaleString()} SuttaCentral entries.`);
                    return;
                }
            } catch (err) {
                console.warn('SuttaCentral catalog source unavailable:', err);
            } finally {
                setCatalogLoading(false);
            }
        }

        // Backend API unavailable — fall back to SuttaCentral's public suttaplex API directly.
        // Subject filtering isn't supported by the public API, so skip that mode.
        if (!options.subject) {
            try {
                setCatalogLoading(true);
                setCatalogStatus('Loading catalog from SuttaCentral...');
                const directItems = await fetchSuttaCentralCatalogDirect({
                    root: options.root || 'mn',
                    lang: language,
                    translator: options.translator,
                });
                if (directItems.length > 0) {
                    setSuttaCentralCatalog(directItems);
                    setCatalogStatus(`Loaded ${directItems.length.toLocaleString()} SuttaCentral entries.`);
                    return;
                }
            } catch (err) {
                console.warn('Direct SuttaCentral catalog fetch failed:', err);
            } finally {
                setCatalogLoading(false);
            }
        }

        setSuttaCentralCatalog(language === 'en' ? SUTTA_CENTRAL_CATALOG : []);
        setCatalogStatus('No matching SuttaCentral entries were found for that load option.');
    }, [language]);

    useEffect(() => {
        fetchLocalSutras();
    }, [fetchLocalSutras]);

    const handleSubmit = (event) => {
        event.preventDefault();
        navigate(`/library/sutras/suttacentral/${encodeURIComponent(uid.trim().toLowerCase())}`);
    };

    const handleCatalogLoad = () => {
        const options = {};

        if (loadMode === 'all') {
            options.root = 'sutta';
        } else if (loadMode === 'collection') {
            options.root = collectionRoot;
        } else if (loadMode === 'subject') {
            options.root = 'sutta';
            options.subject = subjectFilter;
        } else if (loadMode === 'translator') {
            options.root = collectionRoot;
            options.translator = translatorFilter;
        } else if (loadMode === 'length') {
            options.root = lengthRoot;
        }

        fetchSuttaCentralCatalog(options);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setAppliedSearchQuery(searchQuery);
    };

    const handleLocalSutraSelect = (event) => {
        const selected = localSutras.find((item) => item.uuid === event.target.value);
        if (selected) {
            navigate(`/library/sutras/local/${encodeURIComponent(selected.uuid)}`);
        }
    };

    const handleCatalogSelect = (item) => {
        if (item.sourceType === 'local') {
            navigate(`/library/sutras/local/${encodeURIComponent(item.uuid)}`);
            return;
        }

        navigate(`/library/sutras/suttacentral/${encodeURIComponent(item.uid)}`);
    };

    useEffect(() => {
        if (!isDetailPage || localSutras.length === 0) {
            return;
        }

        if (detailSource === 'local') {
            const selected = localSutras.find((item) => item.uuid === detailId);
            if (selected) {
                setSutra(selected);
                setActiveSource('local');
                setError(null);
                return;
            }

            setSutra(null);
            setError('That local sutra could not be found.');
            return;
        }

        if (detailSource === 'suttacentral') {
            fetchSuttaCentralSutra(detailId, author, language);
            return;
        }

        setSutra(null);
        setError('That sutra source is not available.');
    }, [author, detailId, detailSource, fetchSuttaCentralSutra, isDetailPage, language, localSutras]);

    const catalogItems = useMemo(() => [
        ...localSutras
            .filter((item) => language === 'en' || String(item.translation || '').toLowerCase() === language)
            .map((item) => ({
                ...item,
                sourceType: 'local',
                searchText: `${item.title || ''} ${item.short_title || ''} ${item.collection || ''}`.toLowerCase(),
            })),
        ...suttaCentralCatalog,
    ], [localSutras, language, suttaCentralCatalog]);

    const normalizedSearch = appliedSearchQuery.trim().toLowerCase();
    const filteredCatalog = useMemo(
        () => normalizedSearch
            ? catalogItems.filter((item) => item.searchText.includes(normalizedSearch))
            : catalogItems,
        [catalogItems, normalizedSearch]
    );

    const paragraphs = splitFullText(sutra?.full_text);
    const canonicalUrl = sutra?.source?.canonical_url || sutra?.text_link;

    return (
        <div className="min-h-screen py-8">
            {isDetailPage && (
                <Link
                    to="/library/sutras"
                    className="group fixed bottom-5 left-5 z-50 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-blue-700 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:w-48 hover:bg-blue-800 focus:w-48 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                    aria-label="Back to Sutra Library"
                >
                    <span className="text-2xl leading-none transition-transform duration-200 group-hover:-translate-x-1 group-focus:-translate-x-1" aria-hidden="true">
                        &larr;
                    </span>
                    <span className="ml-0 max-w-0 whitespace-nowrap opacity-0 transition-all duration-200 group-hover:ml-2 group-hover:max-w-36 group-hover:opacity-100 group-focus:ml-2 group-focus:max-w-36 group-focus:opacity-100">
                        Back to Library
                    </span>
                </Link>
            )}

            <div className="bg-white bg-opacity-95 rounded-lg shadow-lg p-6 md:p-8 max-w-5xl w-full mx-auto">
                <div className="text-center mb-8">
                    <Link
                        to="/library"
                        className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 mb-4 transition-colors"
                    >
                        <span aria-hidden="true">&larr;</span> Buddhist Library
                    </Link>
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Sutras</h1>
                    <p className="text-gray-500 max-w-xl mx-auto">
                        Curated sutras and public SuttaCentral texts — search the catalog or jump to a featured discourse.
                    </p>
                </div>

                {!isDetailPage && (
                    <section className="mb-8 rounded-lg bg-white p-5 shadow-sm text-left">
                        {/* Search bar row */}
                        <form onSubmit={handleSearchSubmit} className="grid gap-3 lg:grid-cols-[1fr_auto_auto_auto] lg:items-end">
                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-700 mb-2">Search the catalog</span>
                                <input
                                    type="search"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder="Title, UID, or collection…"
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                />
                            </label>

                            <label className="block">
                                <span className="block text-sm font-semibold text-gray-700 mb-2">Language</span>
                                <select
                                    value={language}
                                    onChange={(event) => {
                                        setLanguage(event.target.value);
                                        setSuttaCentralCatalog(event.target.value === 'en' ? SUTTA_CENTRAL_CATALOG : []);
                                    }}
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 lg:w-44"
                                >
                                    {LANGUAGE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <button
                                type="submit"
                                className="self-end rounded-lg bg-blue-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-800"
                            >
                                Search
                            </button>

                            <button
                                type="button"
                                aria-expanded={sourcePanelOpen}
                                aria-controls="sutra-source-panel"
                                onClick={() => setSourcePanelOpen((open) => !open)}
                                className="self-end inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50"
                            >
                                Load Options
                                <span aria-hidden="true" className="text-xs">{sourcePanelOpen ? '▲' : '▼'}</span>
                            </button>
                        </form>

                        {/* Always-visible featured sutras */}
                        <div className="mt-4 flex flex-wrap gap-2">
                            <span className="self-center text-xs font-semibold uppercase tracking-wide text-gray-400">Featured:</span>
                            {FEATURED_SUTRAS.map((item) => (
                                <button
                                    key={item.uid}
                                    type="button"
                                    onClick={() => navigate(`/library/sutras/suttacentral/${encodeURIComponent(item.uid)}`)}
                                    className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 transition-colors hover:bg-blue-100"
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* Collapsible load / source panel */}
                        {sourcePanelOpen && (
                            <div id="sutra-source-panel" className="mt-5 grid gap-5 lg:grid-cols-2">
                                {/* Catalog load options */}
                                <section className="rounded-lg bg-gradient-to-br from-slate-50 to-blue-50 p-5 md:p-6 lg:col-span-2">
                                    <h2 className="mb-1 text-base font-bold text-gray-800">Catalog Load Options</h2>
                                    <p className="mb-4 text-sm text-gray-500">Expand the catalog by loading a collection, subject, or translator from SuttaCentral.</p>
                                    <div className="grid gap-4 lg:grid-cols-[auto_1fr_auto] lg:items-end">
                                        <label className="block">
                                            <span className="block text-sm font-semibold text-gray-700 mb-2">Load by</span>
                                            <select
                                                value={loadMode}
                                                onChange={(event) => setLoadMode(event.target.value)}
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 lg:w-48"
                                            >
                                                {LOAD_MODE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        {loadMode === 'collection' && (
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-700 mb-2">Collection</span>
                                                <select
                                                    value={collectionRoot}
                                                    onChange={(event) => setCollectionRoot(event.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                >
                                                    {COLLECTION_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}

                                        {loadMode === 'subject' && (
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-700 mb-2">Subject</span>
                                                <select
                                                    value={subjectFilter}
                                                    onChange={(event) => setSubjectFilter(event.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                >
                                                    {SUBJECT_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}

                                        {loadMode === 'translator' && (
                                            <div className="grid gap-4 md:grid-cols-2">
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-700 mb-2">Collection Scope</span>
                                                    <select
                                                        value={collectionRoot}
                                                        onChange={(event) => setCollectionRoot(event.target.value)}
                                                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    >
                                                        {COLLECTION_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <label className="block">
                                                    <span className="block text-sm font-semibold text-gray-700 mb-2">Translator</span>
                                                    <input
                                                        type="text"
                                                        value={translatorFilter}
                                                        onChange={(event) => setTranslatorFilter(event.target.value)}
                                                        placeholder="sujato"
                                                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                    />
                                                </label>
                                            </div>
                                        )}

                                        {loadMode === 'length' && (
                                            <label className="block">
                                                <span className="block text-sm font-semibold text-gray-700 mb-2">Length</span>
                                                <select
                                                    value={lengthRoot}
                                                    onChange={(event) => setLengthRoot(event.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                >
                                                    {LENGTH_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}

                                        {loadMode === 'all' && (
                                            <p className="rounded-lg bg-white p-3 text-sm text-gray-600">
                                                Loads the full readable SuttaCentral catalog for the selected language.
                                            </p>
                                        )}

                                        <button
                                            type="button"
                                            onClick={handleCatalogLoad}
                                            disabled={catalogLoading}
                                            className="self-end rounded-lg bg-blue-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-blue-300"
                                        >
                                            {catalogLoading ? 'Loading…' : 'Load Catalog'}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-sm text-gray-500">{catalogStatus}</p>
                                </section>

                                {/* Manual library entries */}
                                <section className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-100 p-5 md:p-6">
                                    <h2 className="mb-1 text-base font-bold text-gray-800">Curated Entries</h2>
                                    <p className="mb-4 text-sm text-gray-500">
                                        Manually added sutras from the site library.
                                    </p>

                                    {localSutras.length > 0 && language === 'en' ? (
                                        <label className="block text-left">
                                            <span className="block text-sm font-semibold text-gray-700 mb-2">Choose a sutra</span>
                                            <select
                                                value={activeSource === 'local' ? sutra?.uuid || '' : ''}
                                                onChange={handleLocalSutraSelect}
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-100"
                                            >
                                                {localSutras.map((item) => (
                                                    <option key={item.uuid} value={item.uuid}>
                                                        {item.title || item.short_title || item.uuid}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    ) : (
                                        <p className="rounded-lg bg-white p-4 text-sm text-gray-600 shadow-sm">
                                            No curated entries available for the selected language.
                                        </p>
                                    )}
                                </section>

                                {/* SuttaCentral UID lookup */}
                                <section className="rounded-lg bg-gradient-to-br from-blue-50 to-slate-100 p-5 md:p-6">
                                    <h2 className="mb-1 text-base font-bold text-gray-800">Direct UID Lookup</h2>
                                    <p className="mb-4 text-sm text-gray-500">
                                        Load any SuttaCentral text directly by its UID. The selected language applies.
                                    </p>

                                    <form onSubmit={handleSubmit} className="grid md:grid-cols-[1fr_auto] gap-4 items-end">
                                        <label className="text-left">
                                            <span className="block text-sm font-semibold text-gray-700 mb-2">SuttaCentral UID</span>
                                            <input
                                                type="text"
                                                value={uid}
                                                onChange={(event) => setUid(event.target.value)}
                                                placeholder="mn1"
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            />
                                        </label>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="rounded-lg bg-blue-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-blue-300"
                                        >
                                            {loading ? 'Loading…' : 'Load'}
                                        </button>
                                    </form>

                                    <div className="mt-4">
                                        <label className="text-left">
                                            <span className="block text-sm font-semibold text-gray-700 mb-2">Translator UID</span>
                                            <input
                                                type="text"
                                                value={author}
                                                onChange={(event) => setAuthor(event.target.value)}
                                                placeholder="sujato"
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                            />
                                        </label>
                                    </div>
                                </section>
                            </div>
                        )}

                        {/* Catalog grid */}
                        {filteredCatalog.length > 0 ? (
                            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                {filteredCatalog.map((item) => (
                                    <button
                                        key={item.uuid || item.uid}
                                        type="button"
                                        onClick={() => handleCatalogSelect(item)}
                                        className="group rounded-lg border border-gray-200 bg-gray-50 p-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <span className="text-xs font-bold uppercase tracking-wider text-blue-700">
                                                {item.sourceType === 'suttacentral' ? item.uid.toUpperCase() : item.short_title || 'Local'}
                                            </span>
                                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                item.sourceType === 'local'
                                                    ? 'bg-amber-100 text-amber-800'
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {item.sourceType === 'local' ? 'Curated' : 'SuttaCentral'}
                                            </span>
                                        </div>
                                        <span className="mt-1 block font-bold text-gray-900 group-hover:text-blue-900">
                                            {item.title || item.short_title || item.uid}
                                        </span>
                                        {item.collection && (
                                            <span className="mt-1 block text-sm text-gray-500">{item.collection}</span>
                                        )}
                                        {item.blurb && (
                                            <span className="mt-2 block text-sm leading-relaxed text-gray-600 line-clamp-2">
                                                {item.blurb}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="mt-5 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
                                <p className="text-gray-500 font-medium">No sutras match your search.</p>
                                <p className="mt-1 text-sm text-gray-400">Try a different term or use Load Options to expand the catalog.</p>
                            </div>
                        )}
                    </section>
                )}

                {error && (
                    <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4 text-left text-red-700">
                        {error}
                    </div>
                )}

                {isDetailPage && loading && !sutra && (
                    <div className="rounded-lg bg-white p-12 text-center shadow">
                        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-700"></div>
                        <p className="text-gray-500">Loading sutra from SuttaCentral…</p>
                    </div>
                )}

                {isDetailPage && sutra && (
                    <article className="rounded-lg bg-gradient-to-br from-blue-50 to-purple-100 p-5 md:p-8 text-left">
                        <header className="mb-8 text-center">
                            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-blue-600">
                                {sutra.short_title || sutra.source?.uid?.toUpperCase()}
                            </p>
                            <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">{sutra.title}</h2>
                            {sutra.transliterated_title && (
                                <p className="mt-2 text-lg italic text-blue-700">{sutra.transliterated_title}</p>
                            )}
                        </header>

                        {/* Metadata strip */}
                        <div className="mb-8 flex flex-wrap gap-3 justify-center">
                            {sutra.collection && (
                                <div className="rounded-lg bg-white px-4 py-3 shadow-sm text-center">
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Collection</p>
                                    <p className="mt-0.5 text-sm font-semibold text-gray-800">{sutra.collection}</p>
                                </div>
                            )}
                            {sutra.translator && (
                                <div className="rounded-lg bg-white px-4 py-3 shadow-sm text-center">
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Translator</p>
                                    <p className="mt-0.5 text-sm font-semibold text-gray-800">{sutra.translator}</p>
                                </div>
                            )}
                            {sutra.estimated_reading_time && (
                                <div className="rounded-lg bg-white px-4 py-3 shadow-sm text-center">
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Reading Time</p>
                                    <p className="mt-0.5 text-sm font-semibold text-gray-800">{sutra.estimated_reading_time}</p>
                                </div>
                            )}
                            {canonicalUrl && (
                                <div className="rounded-lg bg-white px-4 py-3 shadow-sm text-center">
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Source</p>
                                    <a
                                        href={canonicalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-0.5 block text-sm font-semibold text-blue-700 hover:underline"
                                    >
                                        {activeSource === 'suttacentral' ? 'SuttaCentral ↗' : 'Source Text ↗'}
                                    </a>
                                </div>
                            )}
                        </div>

                        {sutra.summary && (
                            <section className="mb-8 rounded-lg bg-white p-5 shadow-sm border-l-4 border-blue-400">
                                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">Summary</h3>
                                <p className="leading-relaxed text-gray-700">{sutra.summary}</p>
                            </section>
                        )}

                        {paragraphs.length > 0 && (
                            <section className="rounded-lg bg-white p-6 md:p-8 shadow-sm">
                                <h3 className="mb-6 text-xs font-bold uppercase tracking-wide text-gray-400 text-center">Text</h3>
                                <div className="mx-auto max-w-prose space-y-6 text-[1.0625rem] leading-8 text-gray-800">
                                    {paragraphs.map((paragraph, index) => (
                                        <p key={`${sutra.uuid || detailId}-${index}`}>{paragraph}</p>
                                    ))}
                                </div>
                            </section>
                        )}
                    </article>
                )}
            </div>
        </div>
    );
};

export default Sutras;
